/**
 * Server-side lookups for the service module pickers (customer + vehicle).
 *
 * The pickers used to scan a locally cached copy of ~1000 customers (and the
 * `vehicles[]` array every one of them was assumed to carry). The backend does
 * not embed vehicles in customers, and a store with thousands of imported
 * customers / vehicles never fits that cache, so every picker now asks the
 * server — `GET /serviceModule/customers?search=` and
 * `GET /serviceModule/vehicles?search=|customerId=` — for a handful of rows.
 *
 * @module helpers/serviceLookupApi
 */
import {apiFetch, normalizeCustomer} from './WorkSheetContext';

const buildQuery = (params) => Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');

const rowsOf = (body) => {
    if (Array.isArray(body)) {
        return body;
    }

    return body?.docs || body?.data || body?.customers || body?.vehicles || [];
};

/**
 * Flatten a vehicle Asset (`brand`, `model`, `serialNumber`, `attributes{…}`) into the
 * flat shape the service screens read (`plate`, `vin`, `year`, `mileage`, …).
 * Already-flat objects pass through unchanged.
 *
 * @param {object} asset Raw vehicle document.
 * @returns {object} Vehicle with both the flat fields and the original document keys.
 */
export const normalizeVehicle = (asset) => {
    if (!asset || typeof asset !== 'object') {
        return asset;
    }

    const attrs = asset.attributes || {};

    return {
        ...asset,
        _id: asset._id || asset.id,
        brand: asset.brand || asset.make || attrs.brand || '',
        model: asset.model || attrs.model || '',
        plate: asset.plate || asset.plateNumber || attrs.plateNumber || '',
        vin: asset.vin || asset.serialNumber || attrs.vin || '',
        year: asset.year || attrs.year || '',
        mileage: asset.mileage ?? attrs.mileage ?? asset.kilometers ?? '',
        fuel: asset.fuel || attrs.fuel || attrs.fuelType || asset.fuelType || '',
        color: asset.color || attrs.color || '',
        engineCode: asset.engineCode || attrs.engineCode || '',
        displacement: asset.displacement || attrs.displacement || attrs.engineDisplacement || '',
        powerKw: asset.powerKw || attrs.powerKw || '',
        powerHp: asset.powerHp || attrs.powerHp || '',
        firstRegistration: asset.firstRegistration || attrs.firstRegistration || '',
        motExpiry: asset.motExpiry || attrs.motExpiry || '',
        type: asset.type || attrs.type || '',
    };
};

/**
 * Search the store's service customers (name, phone, e-mail, plate, VIN, work-order no.).
 *
 * @param {string} term Free text (empty -> first page of the alphabetical list).
 * @param {object} [options]
 * @param {number} [options.page=1] 1-based page.
 * @param {number} [options.limit=6] Page size (the backend caps it).
 * @param {boolean} [options.skipCache=false] Bypass the short GET cache.
 * @returns {Promise<{items: object[], total: number, pages: number}>} Normalised customers.
 */
export const searchServiceCustomers = async (term, {page = 1, limit = 6, skipCache = false} = {}) => {
    const query = buildQuery({page, limit, search: String(term || '').trim() || undefined});
    const body = await apiFetch(`/serviceModule/customers?${query}`, skipCache ? {skipCache: true} : {});
    const items = rowsOf(body).map(normalizeCustomer);
    const total = Number(body?.total);
    const pages = Number(body?.pages);

    return {
        items,
        total: Number.isFinite(total) ? total : items.length,
        pages: Number.isFinite(pages) && pages > 0 ? pages : 1,
    };
};

/**
 * Search / list service vehicles, optionally scoped to one customer.
 *
 * @param {object} [options]
 * @param {string} [options.search] Free text (plate, VIN, brand, model).
 * @param {string} [options.customerId] Only this customer's vehicles.
 * @param {number} [options.page=1] 1-based page.
 * @param {number} [options.limit=12] Page size (the backend caps it).
 * @returns {Promise<{items: object[], total: number, pages: number}>} Normalised vehicles;
 *   `customerId` stays the populated owner object when the backend populated it.
 */
export const searchServiceVehicles = async ({search = '', customerId, page = 1, limit = 12} = {}) => {
    const query = buildQuery({page, limit, search: String(search || '').trim() || undefined, customerId});
    const body = await apiFetch(`/serviceModule/vehicles?${query}`);
    const items = rowsOf(body).map(normalizeVehicle);
    const total = Number(body?.total);
    const pages = Number(body?.pages);

    return {
        items,
        total: Number.isFinite(total) ? total : items.length,
        pages: Number.isFinite(pages) && pages > 0 ? pages : 1,
    };
};

/**
 * The owner of a vehicle row as a normalised customer, or `null` when the
 * backend did not populate `customerId`.
 *
 * @param {object} vehicle Normalised vehicle from {@link searchServiceVehicles}.
 * @returns {object|null} Normalised customer.
 */
export const ownerOfVehicle = (vehicle) => (
    vehicle?.customerId && typeof vehicle.customerId === 'object' ? normalizeCustomer(vehicle.customerId) : null
);

const isServerId = (id) => /^[a-f0-9]{24}$/i.test(String(id || ''));

/**
 * Build the vehicle request body (`POST|PATCH /serviceModule/vehicles`) from the flat
 * draft the vehicle editor holds. Technical fields are also mirrored into
 * `attributes`, where the backend keeps and returns them.
 *
 * @param {object} vehicle Flat editor draft (`plate`, `brand`, `model`, `vin`, `mileage`, `year`, …).
 * @returns {object} Request body without the owner (added on create).
 */
export const buildVehiclePayload = (vehicle = {}) => ({
    plateNumber: vehicle.plate || vehicle.plateNumber || '',
    make: vehicle.brand || '',
    brand: vehicle.brand || '',
    model: vehicle.model || '',
    vin: vehicle.vin || '',
    mileage: Number(vehicle.mileage) || 0,
    fuelType: vehicle.fuel || '',
    attributes: {
        type: vehicle.type || '',
        year: vehicle.year ? String(vehicle.year) : '',
        fuel: vehicle.fuel || '',
        color: vehicle.color || '',
        engineCode: vehicle.engineCode || '',
        displacement: vehicle.displacement || '',
        powerKw: vehicle.powerKw || '',
        powerHp: vehicle.powerHp || '',
        firstRegistration: vehicle.firstRegistration || '',
        motExpiry: vehicle.motExpiry || '',
    },
});

/**
 * Create (new / unsynced id) or update (server id) a customer's vehicle.
 *
 * @param {string} customerId Owner customer id (required on create).
 * @param {object} vehicle Flat editor draft; `_id` selects update when it is a server id.
 * @returns {Promise<object>} The saved vehicle, normalised.
 */
export const saveServiceVehicle = async (customerId, vehicle) => {
    const payload = buildVehiclePayload(vehicle);
    const saved = isServerId(vehicle?._id)
        ? await apiFetch(`/serviceModule/vehicles/${vehicle._id}`, {method: 'PATCH', body: JSON.stringify(payload)})
        : await apiFetch('/serviceModule/vehicles', {method: 'POST', body: JSON.stringify({...payload, customerId})});

    return normalizeVehicle(saved);
};

/**
 * Archive (soft-delete) a vehicle.
 *
 * @param {string} vehicleId Vehicle id.
 * @returns {Promise<void>}
 */
export const removeServiceVehicle = async (vehicleId) => {
    await apiFetch(`/serviceModule/vehicles/${vehicleId}`, {method: 'DELETE'});
};
